import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { sessionStorage } from '@/services/api/sessionStorage'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid Date'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid Date'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

export function formatPower(watts: number): string {
  if (Math.abs(watts) >= 1000000) {
    return `${(watts / 1000000).toFixed(2)} MW`
  } else if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`
  }
  return `${watts.toFixed(0)} W`
}

export function formatEnergy(wattHours: number): string {
  if (Math.abs(wattHours) >= 1000000) {
    return `${(wattHours / 1000000).toFixed(2)} MWh`
  } else if (Math.abs(wattHours) >= 1000) {
    return `${(wattHours / 1000).toFixed(2)} kWh`
  }
  return `${wattHours.toFixed(0)} Wh`
}

export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

/**
 * Convert an interval energy value (Wh) to average power (W) over the given
 * interval duration in milliseconds.
 *
 *   power (W) = energy (Wh) / duration (h)
 *             = energy (Wh) / (durationMs / 3_600_000)
 *
 * For a standard 15-minute interval: power = energy × 4.
 */
export function energyToIntervalPower(wattHours: number, durationMs: number): number {
  if (durationMs <= 0) return 0
  const durationH = durationMs / 3_600_000
  return wattHours / durationH
}

export function xenonSystemUrl(systemID: string): string {
  const base =
    sessionStorage.getEnvironment() === 'production'
      ? 'https://xenon-next.gridx.de'
      : 'https://xenon-next.staging.gridx.de'
  return `${base}/systems/${systemID}/live-view`
}
